import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  getImpersonationToken,
  getImpersonationMeta,
  setImpersonation as persistImpersonation,
  clearImpersonation,
  IMP_EVENTS,
} from "../utils/impersonationFetch";

const API_URL = "/api";

const ImpersonationContext = createContext(null);

/**
 * Provider de estado de impersonation para toda la web.
 *
 * Expone:
 * - isActive, sessionId, actingAsUser, originalUser, expiresAt, remainingMs
 * - start(targetUserId, motivo, duracionHoras, notas)
 * - stop()
 * - refreshMe()
 * - listAvailable()
 * - listAudit(sessionId)
 *
 * Cómo se usa:
 * 1) Envuelve <App /> con <ImpersonationProvider userId={...}>.
 * 2) En cualquier componente: const { isActive, ... } = useImpersonation().
 * 3) El header X-Impersonation-Token se inyecta automáticamente en todas
 *    las requests al backend (gracias a installImpersonationFetch()).
 */
export function ImpersonationProvider({ userId, children }) {
  const [token, setTokenState] = useState(() => getImpersonationToken());
  const [meta, setMetaState] = useState(() => getImpersonationMeta());
  const [lastError, setLastError] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Re-render cada minuto para que el banner muestre el countdown actualizado.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Sincroniza el estado con cambios externos (otra tab, expiración, etc.)
  useEffect(() => {
    function onChanged(e) {
      const { token: t, meta: m } = e.detail || {};
      setTokenState(t || null);
      setMetaState(m || null);
    }
    function onError(e) {
      const { code, message } = e.detail || {};
      setLastError({ code, message, at: Date.now() });
    }
    window.addEventListener(IMP_EVENTS.CHANGED, onChanged);
    window.addEventListener(IMP_EVENTS.ERROR, onError);
    return () => {
      window.removeEventListener(IMP_EVENTS.CHANGED, onChanged);
      window.removeEventListener(IMP_EVENTS.ERROR, onError);
    };
  }, []);

  // Si hay token al montar, valida con el backend (puede haber expirado entre
  // sesiones de la tab — hot-reload o login fresco).
  // Si el usuario cierra sesión (userId pasa a null), limpiamos el token local.
  useEffect(() => {
    if (token && userId) {
      refreshMe().catch(() => {});
    } else if (token && !userId) {
      clearImpersonation("logout");
      setTokenState(null);
      setMetaState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (userId) h["X-User-Id"] = String(userId);
    return h;
  }, [userId]);

  const refreshMe = useCallback(async () => {
    if (!userId) return null;
    const r = await fetch(`${API_URL}/admin/impersonate/me`, { headers });
    if (!r.ok) {
      // 401/403 con código IMPERSONATION_* ya fue manejado por el interceptor
      return null;
    }
    const data = await r.json();
    if (data?.is_impersonating) {
      const newMeta = {
        sessionId: data.session_id,
        actingAsUser: data.acting_as_user,
        originalUser: data.original_user,
        startedAt: data.started_at,
        expiresAt: data.expires_at,
      };
      persistImpersonation(getImpersonationToken(), newMeta);
      setMetaState(newMeta);
      return newMeta;
    }
    // Backend dice que no hay sesión activa — limpiar local
    clearImpersonation("server_says_inactive");
    return null;
  }, [userId, headers]);

  const start = useCallback(
    async (targetUserId, motivo = "pruebas", duracionHoras = 4, notas = null) => {
      const r = await fetch(`${API_URL}/admin/impersonate/start/${targetUserId}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          motivo,
          duracion_horas: duracionHoras,
          notas: notas || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        const detail = data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        const message = typeof detail === "string" ? detail : detail?.message || "Error al iniciar impersonation";
        const err = new Error(message);
        err.status = r.status;
        err.code = code;
        err.payload = detail;
        throw err;
      }
      const newMeta = {
        sessionId: data.session_id,
        actingAsUser: data.acting_as_user,
        originalUser: { id: data.original_user_id, name: null },
        startedAt: data.started_at,
        expiresAt: data.expires_at,
      };
      persistImpersonation(data.impersonation_token, newMeta);
      setTokenState(data.impersonation_token);
      setMetaState(newMeta);
      return data;
    },
    [headers],
  );

  const stop = useCallback(async () => {
    try {
      await fetch(`${API_URL}/admin/impersonate/stop`, { method: "POST", headers });
    } catch (_) {}
    clearImpersonation("manual_stop");
    setTokenState(null);
    setMetaState(null);
  }, [headers]);

  const listAvailable = useCallback(async () => {
    const r = await fetch(`${API_URL}/admin/impersonate/available`, { headers });
    if (!r.ok) {
      const err = new Error(r.status === 403 ? "Solo superadmins" : `Error ${r.status}`);
      err.status = r.status;
      throw err;
    }
    const data = await r.json();
    return data.users || [];
  }, [headers]);

  const listAudit = useCallback(
    async (sessionId = null) => {
      const qs = sessionId ? `?session_id=${sessionId}` : "";
      const r = await fetch(`${API_URL}/admin/impersonate/audit${qs}`, { headers });
      if (!r.ok) {
        const err = new Error(`Error ${r.status}`);
        err.status = r.status;
        throw err;
      }
      return r.json();
    },
    [headers],
  );

  const expiresAtMs = meta?.expiresAt ? new Date(meta.expiresAt).getTime() : null;
  const remainingMs = expiresAtMs ? Math.max(0, expiresAtMs - now) : null;

  const value = useMemo(
    () => ({
      isActive: !!token,
      token,
      sessionId: meta?.sessionId || null,
      actingAsUser: meta?.actingAsUser || null,
      originalUser: meta?.originalUser || null,
      startedAt: meta?.startedAt || null,
      expiresAt: meta?.expiresAt || null,
      remainingMs,
      lastError,
      start,
      stop,
      refreshMe,
      listAvailable,
      listAudit,
    }),
    [token, meta, remainingMs, lastError, start, stop, refreshMe, listAvailable, listAudit],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    // Fallback "no-op" si alguien lo usa fuera del provider.
    return {
      isActive: false,
      token: null,
      sessionId: null,
      actingAsUser: null,
      originalUser: null,
      startedAt: null,
      expiresAt: null,
      remainingMs: null,
      lastError: null,
      start: async () => { throw new Error("ImpersonationProvider missing"); },
      stop: async () => {},
      refreshMe: async () => null,
      listAvailable: async () => [],
      listAudit: async () => ({}),
    };
  }
  return ctx;
}
